import { Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import type { ModuleProgressEntry } from '../../lib/types';
import { moduleFriendlyName } from '../../lib/module-names';

export interface RunProgressProps {
  progressPct: number;
  currentModule: string | null;
  completedModules: ModuleProgressEntry[];
  estimatedCompletion: string | null;
  /** Injectable for tests; defaults to the real clock — only used to render a relative "time remaining" estimate. */
  now?: () => number;
}

/** "1.2s" for a second or more, "340ms" below that. */
function formatDuration(durationMs: number): string {
  if (durationMs >= 1000) return `${(durationMs / 1000).toFixed(1)}s`;
  return `${Math.round(durationMs)}ms`;
}

/** "~2 min remaining" from an ISO timestamp, relative to `now`. Never negative-looking to the user. */
function formatEtaRemaining(estimatedCompletion: string, now: number): string {
  const remainingMs = new Date(estimatedCompletion).getTime() - now;
  if (remainingMs <= 0) return 'finishing up';
  const remainingMin = Math.round(remainingMs / 60000);
  if (remainingMin < 1) return '<1 min remaining';
  return `~${remainingMin} min remaining`;
}

/**
 * Module-level run progress display (BUI-988): a progress bar, "Currently
 * running: [module]" while one is in flight, a completed-modules list with a
 * checkmark and duration, and an estimated-time-remaining string. Purely a
 * display of `Run`'s progress fields — never drives any polling/mutation
 * itself, the caller (`RunDetail`) owns that.
 */
export function RunProgress({ progressPct, currentModule, completedModules, estimatedCompletion, now = Date.now }: RunProgressProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Module progress</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-ink-muted">
            <span>{progressPct}%</span>
            {estimatedCompletion ? <span>{formatEtaRemaining(estimatedCompletion, now())}</span> : null}
          </div>
          <div
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
          >
            <div
              className="h-full rounded-full bg-brand transition-all"
              style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
            />
          </div>
        </div>

        {currentModule ? (
          <p className="text-sm text-ink">
            <span className="text-ink-muted">Currently running:</span>{' '}
            <span className="font-medium">{moduleFriendlyName(currentModule)}</span>
          </p>
        ) : null}

        {completedModules.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {completedModules.map((m, i) => (
              <li key={`${m.moduleId}-${i}`} className="flex items-center gap-2">
                <Check size={14} className="shrink-0 text-brand-hover" />
                <span className="text-ink">{moduleFriendlyName(m.moduleId)}</span>
                <span className="text-xs text-ink-faint">{formatDuration(m.durationMs)}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
